import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { designSystem, Modal } from '@yola/ws-ui';
import DifmOrderFormSteps from './difm-order-form-steps';
import DifmOrderFormHeader from './difm-order-form-header';
import DifmOrderFormFooter from './difm-order-form-footer';
import DifmOrderFormFieldsSection from './difm-order-form-fields-section';
import DifmOrderFormSummarySection from './difm-order-form-summary-section';
import setupSteps from '../constants/setup-steps';

const { Divider, Box, Stack, Heading, Paragraph } = designSystem;

const MODAL_WIDTH = 886;
const MODAL_HEIGHT = 650;

const DifmOrderFormDialog = ({
  captions,
  activeStepId,
  steps,
  activeStepFieldIds,
  activeStepFieldNames,
  fields,
  control,
  summaryData,
  isDesktopView,
  showBackButton,
  showSubmitButton,
  scrollableContainerRef,
  getValues,
  onBack,
  onNext,
  onChange,
  onEdit,
  onClose,
  onSubmit,
}) => (
  <Modal
    isBodyScrollDisabled
    centered
    resizable={false}
    draggable={false}
    width={MODAL_WIDTH}
    height={MODAL_HEIGHT}
    fullscreen={!isDesktopView}
    overlay="visible"
    className="ws-difm-order-form"
  >
    <div
      className={classNames('ws-difm-order-form-layout', {
        'ws-difm-order-form-layout--desktop-view': isDesktopView,
      })}
      {...(!isDesktopView && { ref: scrollableContainerRef })}
    >
      <div className="ws-difm-order-form-layout__header">
        <DifmOrderFormHeader
          backButtonLabel={captions.backButtonLabel}
          showBackButton={showBackButton && !isDesktopView}
          isDesktopView={isDesktopView}
          onBackButtonClick={onBack}
          onCloseButtonClick={onClose}
        />
      </div>
      <div className="ws-difm-order-form-layout__steps">
        <DifmOrderFormSteps
          captions={captions.stepsPanel}
          steps={steps}
          isDesktopView={isDesktopView}
          onStepButtonClick={onChange}
        />
      </div>
      <div
        className="ws-difm-order-form-layout__content"
        {...(isDesktopView && { ref: scrollableContainerRef })}
      >
        <Box
          {...(isDesktopView
            ? {
                paddingLeft: 'spacing-xl',
                paddingRight: 'spacing-xl',
                paddingBottom: 'spacing-xl',
              }
            : {
                padding: 'spacing-xs',
                paddingTop: 'spacing-m',
                paddingBottom: 'spacing-m',
              })}
        >
          <Stack gap="spacing-m">
            <Stack gap="spacing-3-xs">
              <Heading type="heading-3">{captions[activeStepId].sectionTitle}</Heading>
              <Paragraph>{captions[activeStepId].sectionDescription}</Paragraph>
            </Stack>
            {[setupSteps.DETAILS, setupSteps.PROJECT, setupSteps.CONTACT_INFO].includes(
              activeStepId
            ) && (
              <DifmOrderFormFieldsSection
                activeStepFieldIds={activeStepFieldIds}
                activeStepFieldNames={activeStepFieldNames}
                control={control}
                getValues={getValues}
                fields={fields}
              />
            )}
            {activeStepId === setupSteps.SUMMARY && (
              <DifmOrderFormSummarySection
                captions={captions[setupSteps.SUMMARY]}
                summaryData={summaryData}
                isDesktopView={isDesktopView}
                onEditButtonClick={onEdit}
              />
            )}
          </Stack>
        </Box>
      </div>
      <div className="ws-difm-order-form-layout__footer">
        <Divider />
        <DifmOrderFormFooter
          backButtonLabel={captions.backButtonLabel}
          submitButtonLabel={captions.submitButtonLabel}
          nextButtonLabel={captions.nextButtonLabel}
          showBackButton={showBackButton && isDesktopView}
          showSubmitButton={showSubmitButton}
          isDesktopView={isDesktopView}
          onBackButtonClick={onBack}
          onSubmitButtonClick={onSubmit}
          onNextButtonClick={onNext}
        />
      </div>
    </div>
  </Modal>
);

DifmOrderFormDialog.propTypes = {
  captions: PropTypes.shape().isRequired,
  activeStepId: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  activeStepFieldIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeStepFieldNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  control: PropTypes.shape({}).isRequired,
  summaryData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isDesktopView: PropTypes.bool.isRequired,
  showBackButton: PropTypes.bool.isRequired,
  showSubmitButton: PropTypes.bool.isRequired,
  scrollableContainerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
  getValues: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default DifmOrderFormDialog;
